import api from "./api";
import type { ValorCarradaVigencia } from "./locacoes";

export type ApontadorStatus = "ativo" | "inativo";

export type ApontadorTipo = "jazida" | "obra";

export const APONTADOR_TIPO_LABELS: Record<ApontadorTipo, string> = {
  jazida: "Apontador Jazida",
  obra: "Apontador Obra",
};
export type FrequenciaSituacao =
  | "presente"
  | "falta"
  | "atestado"
  | "folga"
  | "meio_periodo";

export const FREQUENCIA_SITUACOES: FrequenciaSituacao[] = [
  "presente",
  "falta",
  "atestado",
  "folga",
  "meio_periodo",
];

export const FREQUENCIA_SITUACAO_LABELS: Record<FrequenciaSituacao, string> = {
  presente: "Presente",
  falta: "Falta",
  atestado: "Atestado",
  folga: "Folga",
  meio_periodo: "Meio período",
};

/** Materiais disponíveis no cadastro de carradas (aba Apropriação). */
export const CARRADA_MATERIAIS = [
  "Barro",
  "Areia",
  "Brita",
  "Saibro",
  "Cascalho",
  "Terra",
  "Entulho",
  "Outro",
] as const;

export type CarradaMaterial = (typeof CARRADA_MATERIAIS)[number];

export interface ApontadorDto {
  id: string;
  nome: string;
  telefone: string;
  cpf: string;
  funcao: string;
  tipoApontador: ApontadorTipo;
  status: ApontadorStatus;
  mobileEnabled: boolean;
  contratoIdsPermitidos: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ApontadorFormValues {
  nome: string;
  telefone: string;
  cpf: string;
  funcao: string;
  tipoApontador: ApontadorTipo;
  status: ApontadorStatus;
  mobileEnabled: boolean;
  contratoIdsPermitidos: string[];
}

export interface FrequenciaDto {
  id: string;
  apontadorId: string;
  data: string;
  situacao: FrequenciaSituacao;
  valorDiaria: number | null;
  observacao: string;
  updatedAt: string;
}

export interface CarradaFotoDto {
  id: string;
  storedFile: string;
  originalName: string;
  mimeType: string;
  uploadedAt: string;
  usuarioId: string;
  usuarioNome: string;
  /** Presente em anexos do relatório agrupado (Jazida × Obra). */
  lancamentoId?: string;
}

export interface CarradaDto {
  id: string;
  contratoId: string;
  caminhaoId: string;
  data: string;
  quantidade: number;
  valorUnitario: number;
  valorVigenciaId?: string;
  material: string;
  motorista: string;
  observacao: string;
  fotos: CarradaFotoDto[];
  usuarioId: string;
  usuarioNome: string;
  horaRegistro: string;
  origem?: "jazida" | "obra" | "manual";
  statusAprovacao?: "pendente" | "aprovado" | "rejeitado";
  chavePareamento?: string;
  /** Mesmo valor na Jazida e na Obra da mesma viagem. */
  parId?: string;
  apontadorId?: string;
}

/** Igual a CARRADA_PAR_DESDE em src/lib/carradasTypes.ts. */
export const CARRADA_PAR_DESDE = "2026-10-08";

export type CarradaViagemSituacao =
  | "conferido"
  | "qtd_diferente"
  | "so_jazida"
  | "so_obra"
  | "manual";

export const SITUACAO_VIAGEM_LABELS: Record<CarradaViagemSituacao, string> = {
  conferido: "Conferido",
  qtd_diferente: "Qtd diferente",
  so_jazida: "Só Jazida",
  so_obra: "Só Obra",
  manual: "Manual",
};

export interface CarradaViagem {
  id: string;
  data: string;
  caminhaoId: string;
  material: string;
  horaRegistro: string;
  jazida?: CarradaDto;
  obra?: CarradaDto;
  manual?: CarradaDto;
  lancamentos: CarradaDto[];
  situacao: CarradaViagemSituacao;
}

function chaveDiaCarrada(c: CarradaDto): string {
  return (
    c.chavePareamento ||
    [c.data.slice(0, 10), c.contratoId, c.caminhaoId, (c.material || "").trim().toLowerCase()].join("|")
  );
}

/** Mesma regra do servidor: conta a viagem uma vez. */
export function quantidadeFaturavelCarradaDto(c: CarradaDto, todas: CarradaDto[]): number {
  if (c.statusAprovacao && c.statusAprovacao !== "aprovado") return 0;
  const origem = c.origem ?? "manual";
  if (origem === "manual" || origem === "obra") return c.quantidade || 0;
  const parId = c.parId?.trim();
  if (parId) {
    const temPar = todas.some(
      (o) =>
        o.id !== c.id &&
        o.parId === parId &&
        o.origem === "obra" &&
        (!o.statusAprovacao || o.statusAprovacao === "aprovado"),
    );
    return temPar ? 0 : c.quantidade || 0;
  }
  if (c.data < CARRADA_PAR_DESDE) {
    const chave = chaveDiaCarrada(c);
    const temObra = todas.some(
      (o) =>
        o.id !== c.id &&
        chaveDiaCarrada(o) === chave &&
        o.origem === "obra" &&
        (!o.statusAprovacao || o.statusAprovacao === "aprovado"),
    );
    return temObra ? 0 : c.quantidade || 0;
  }
  return c.quantidade || 0;
}

export function groupCarradasEmViagens(carradas: CarradaDto[]): CarradaViagem[] {
  const groups = new Map<string, CarradaDto[]>();
  for (const c of carradas) {
    const key = c.parId?.trim() ? `par:${c.parId.trim()}` : `id:${c.id}`;
    const list = groups.get(key) ?? [];
    list.push(c);
    groups.set(key, list);
  }
  const viagens: CarradaViagem[] = [];
  for (const [id, items] of groups) {
    const jazida = items.find((c) => c.origem === "jazida");
    const obra = items.find((c) => c.origem === "obra");
    const manual = items.find((c) => (c.origem ?? "manual") === "manual");
    const ref = [...items].sort((a, b) => b.horaRegistro.localeCompare(a.horaRegistro))[0];
    let situacao: CarradaViagemSituacao = "manual";
    if (jazida && obra) {
      situacao = jazida.quantidade === obra.quantidade ? "conferido" : "qtd_diferente";
    } else if (jazida) situacao = "so_jazida";
    else if (obra) situacao = "so_obra";
    viagens.push({
      id,
      data: ref.data,
      caminhaoId: ref.caminhaoId,
      material: ref.material,
      horaRegistro: ref.horaRegistro,
      jazida,
      obra,
      manual,
      lancamentos: items,
      situacao,
    });
  }
  viagens.sort((a, b) => {
    const byDate = b.data.localeCompare(a.data);
    if (byDate !== 0) return byDate;
    return b.horaRegistro.localeCompare(a.horaRegistro);
  });
  return viagens;
}

export function lancamentoPrincipalViagem(v: CarradaViagem): CarradaDto {
  return v.obra ?? v.jazida ?? v.manual ?? v.lancamentos[0];
}

export interface CarradaFormValues {
  contratoId: string;
  caminhaoId: string;
  data: string;
  quantidade: number;
  material: string;
  motorista: string;
  observacao: string;
}

export interface ContratoCarradasDto {
  id: string;
  codigoContrato: string;
  clienteSiengeNome: string;
  maquinaIds: string[];
  valorCarradaVigencias: ValorCarradaVigencia[];
  dataInicio: string;
  dataFimPrevista: string | null;
}

export interface CaminhaoCarradasDto {
  id: string;
  nome: string;
  codigoInterno: string;
  tipo: string;
  placa: string;
  status: string;
}

export interface CarradasDashboardResumoDto {
  totalCaminhoes: number;
  totalMotoristas: number;
  carradasHoje: number;
  apontadoresAtivos: number;
  caminhaoDestaqueHoje: {
    caminhaoId: string;
    caminhaoNome: string;
    quantidade: number;
  } | null;
  serieCarradasPorCaminhao: Array<{
    caminhaoId: string;
    caminhaoNome: string;
    total: number;
  }>;
  serieProducaoDiaria: Array<{ data: string; quantidade: number }>;
}

export interface RelatorioFrequenciaDto {
  apontador: ApontadorDto;
  dataInicial: string;
  dataFinal: string;
  indicadores: {
    totalRegistros: number;
    presentes: number;
    faltas: number;
    folgas: number;
    atestados: number;
    meioPeriodo: number;
    diasTrabalhados: number;
    percentualPresenca: number;
    valorDiarias: number;
  };
  detalhe: FrequenciaDto[];
}

export type ComparativoCarrada =
  | "igual"
  | "obra_menor"
  | "obra_maior"
  | "incompleto"
  | "manual";

export type CarradaAprovacaoStatus = "pendente" | "aprovado" | "rejeitado";

export const COMPARATIVO_CARRADA_LABELS: Record<ComparativoCarrada, string> = {
  igual: "Conferido",
  obra_menor: "Obra < Jazida",
  obra_maior: "Obra > Jazida",
  incompleto: "Aguardando par",
  manual: "Lançamento manual",
};

export interface RelatorioCarradasLinhaDto {
  id: string;
  chavePareamento: string;
  data: string;
  maquinaId: string;
  maquinaNome: string;
  operador: string;
  placa: string;
  material: string;
  quantidadeJazida: number;
  quantidadeObra: number;
  quantidade: number;
  comparativo: ComparativoCarrada;
  statusAprovacao: CarradaAprovacaoStatus;
  valorUnitario: number;
  valorTotal: number;
  anexos: CarradaFotoDto[];
  contratoId: string;
  lancamentoIds: string[];
}

export interface RelatorioCarradasDto {
  dataInicial: string;
  dataFinal: string;
  caminhaoId: string | null;
  material: string | null;
  indicadores: {
    totalLancamentos: number;
    totalCarradas: number;
    totalMaquinas: number;
    totalAnexos: number;
    valorTotal: number;
  };
  resumoPorCaminhao: Array<{
    maquinaId: string;
    maquinaNome: string;
    placa: string;
    totalCarradas: number;
    valorTotal: number;
  }>;
  detalhe: RelatorioCarradasLinhaDto[];
}

function unwrapError(data: { success?: boolean; error?: string }): void {
  if (data && data.success === false) {
    throw new Error(data.error || "Erro na requisição Carradas.");
  }
}

export async function fetchCarradasDashboard(): Promise<CarradasDashboardResumoDto> {
  const { data } = await api.get<{ success: boolean; resumo: CarradasDashboardResumoDto; error?: string }>(
    "/api/locacoes/carradas/dashboard/resumo",
  );
  unwrapError(data);
  return data.resumo;
}

export async function fetchApontadores(params?: {
  q?: string;
  status?: ApontadorStatus;
}): Promise<ApontadorDto[]> {
  const { data } = await api.get<{ success: boolean; apontadores: ApontadorDto[]; error?: string }>(
    "/api/locacoes/carradas/apontadores",
    { params },
  );
  unwrapError(data);
  return data.apontadores;
}

export async function createApontador(body: ApontadorFormValues): Promise<ApontadorDto> {
  const { data } = await api.post<{ success: boolean; apontador: ApontadorDto; error?: string }>(
    "/api/locacoes/carradas/apontadores",
    body,
  );
  unwrapError(data);
  return data.apontador;
}

export async function updateApontador(
  id: string,
  body: Partial<ApontadorFormValues>,
): Promise<ApontadorDto> {
  const { data } = await api.patch<{ success: boolean; apontador: ApontadorDto; error?: string }>(
    `/api/locacoes/carradas/apontadores/${id}`,
    body,
  );
  unwrapError(data);
  return data.apontador;
}

export async function deleteApontador(id: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(
    `/api/locacoes/carradas/apontadores/${id}`,
  );
  unwrapError(data);
}

export async function fetchFrequenciaByData(dataDia: string): Promise<FrequenciaDto[]> {
  const { data } = await api.get<{ success: boolean; frequencia: FrequenciaDto[]; error?: string }>(
    "/api/locacoes/carradas/frequencia",
    { params: { data: dataDia } },
  );
  unwrapError(data);
  return data.frequencia;
}

export async function saveFrequenciaLote(
  dataDia: string,
  itens: Array<{
    apontadorId: string;
    situacao: FrequenciaSituacao;
    valorDiaria?: number | null;
    observacao?: string;
  }>,
): Promise<FrequenciaDto[]> {
  const { data } = await api.post<{ success: boolean; frequencia: FrequenciaDto[]; error?: string }>(
    "/api/locacoes/carradas/frequencia/lote",
    { data: dataDia, itens },
  );
  unwrapError(data);
  return data.frequencia;
}

export async function deleteFrequencia(id: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(
    `/api/locacoes/carradas/frequencia/${id}`,
  );
  unwrapError(data);
}

export async function fetchRelatorioFrequencia(params: {
  apontadorId: string;
  dataInicial: string;
  dataFinal: string;
}): Promise<RelatorioFrequenciaDto> {
  const { data } = await api.get<{
    success: boolean;
    relatorio: RelatorioFrequenciaDto;
    error?: string;
  }>("/api/locacoes/carradas/relatorios/frequencia", { params });
  unwrapError(data);
  return data.relatorio;
}

export async function fetchRelatorioCarradas(params: {
  dataInicial: string;
  dataFinal: string;
  caminhaoId?: string;
  material?: string;
}): Promise<RelatorioCarradasDto> {
  const { data } = await api.get<{
    success: boolean;
    relatorio: RelatorioCarradasDto;
    error?: string;
  }>("/api/locacoes/carradas/relatorios/carradas", { params });
  unwrapError(data);
  return data.relatorio;
}

export async function aprovarPareamentoCarrada(chavePareamento: string): Promise<void> {
  const { data } = await api.post<{ success: boolean; error?: string }>(
    "/api/locacoes/carradas/pareamentos/aprovar",
    { chavePareamento },
  );
  unwrapError(data);
}

export async function rejeitarPareamentoCarrada(
  chavePareamento: string,
  motivo: string,
): Promise<void> {
  const { data } = await api.post<{ success: boolean; error?: string }>(
    "/api/locacoes/carradas/pareamentos/rejeitar",
    { chavePareamento, motivo },
  );
  unwrapError(data);
}

export async function fetchCaminhoesCarradas(): Promise<CaminhaoCarradasDto[]> {
  const { data } = await api.get<{ success: boolean; caminhoes: CaminhaoCarradasDto[]; error?: string }>(
    "/api/locacoes/carradas/caminhoes",
  );
  unwrapError(data);
  return data.caminhoes;
}

export async function fetchContratosCarradas(): Promise<ContratoCarradasDto[]> {
  const { data } = await api.get<{ success: boolean; contratos: ContratoCarradasDto[]; error?: string }>(
    "/api/locacoes/carradas/contratos",
  );
  unwrapError(data);
  return data.contratos;
}

export async function fetchCarradasLancamentos(params?: {
  dataInicial?: string;
  dataFinal?: string;
  caminhaoId?: string;
  contratoId?: string;
  material?: string;
}): Promise<CarradaDto[]> {
  const { data } = await api.get<{ success: boolean; carradas: CarradaDto[]; error?: string }>(
    "/api/locacoes/carradas/lancamentos",
    { params },
  );
  unwrapError(data);
  return data.carradas;
}

export async function createCarrada(body: CarradaFormValues): Promise<CarradaDto> {
  const { data } = await api.post<{ success: boolean; carrada: CarradaDto; error?: string }>(
    "/api/locacoes/carradas/lancamentos",
    body,
  );
  unwrapError(data);
  return data.carrada;
}

export async function updateCarrada(id: string, body: Partial<CarradaFormValues>): Promise<CarradaDto> {
  const { data } = await api.patch<{ success: boolean; carrada: CarradaDto; error?: string }>(
    `/api/locacoes/carradas/lancamentos/${id}`,
    body,
  );
  unwrapError(data);
  return data.carrada;
}

export async function deleteCarrada(id: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(
    `/api/locacoes/carradas/lancamentos/${id}`,
  );
  unwrapError(data);
}

export async function uploadCarradaFoto(carradaId: string, arquivo: File): Promise<CarradaFotoDto> {
  const fd = new FormData();
  fd.append("arquivo", arquivo);
  const { data } = await api.post<{ success: boolean; foto: CarradaFotoDto; error?: string }>(
    `/api/locacoes/carradas/lancamentos/${carradaId}/fotos`,
    fd,
  );
  unwrapError(data);
  return data.foto;
}

export async function deleteCarradaFoto(carradaId: string, fotoId: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(
    `/api/locacoes/carradas/lancamentos/${carradaId}/fotos/${fotoId}`,
  );
  unwrapError(data);
}

export function carradaFotoUrl(carradaId: string, fotoId: string): string {
  return `/api/locacoes/carradas/lancamentos/${carradaId}/fotos/${fotoId}`;
}

export function formatCpfDisplay(cpf: string): string {
  const d = cpf.replace(/\D/g, "").slice(0, 11);
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}
